import { connect, injectReducer, injectSaga } from '@vfde-sails/core';
import saga from './saga';
import reducer from './reducer';
import * as mapDispatch from './action';
import { selectors as mapStateToProps } from './selector';
import tariffSelection from 'Container/TariffSelection';
import discountSection from 'Container/DiscountSection';
import availabilityCheck from 'Container/AvailabilityCheck';
import priceSection from 'Container/PriceSection';
import leadManagementForm from 'Container/LeadManagementForm';
import { mountLoadingSpinner, updateLoadingSpinner } from '../../components/loadingSpinner';
import {
    AddressHtmlId,
    CheckModuleHtmlId,
    LMModuleHtmlId,
    AppHtmlId,
} from '../../enums/elementId';
import { TrackingAvcStep } from '../../enums/tracking';
import { APP_NAMESPACE } from './constants';
import { getUpdateFlags, loadInitialAppState, saveStateToSession } from './util/state';
import { updateOverlayContent } from '../../components/overlayContent';
import { mountOverlayComponent } from '../../components/overlay';
import { AccordionType, mountAccordion, updateAccordionFootnote } from '../../components/accordion';
import { mountText, updateAddressText } from '../../components/justText';
import { trackPageView } from '../../util/tracking';
import { Accordion } from '@vfde-brix/ws10/accordion';
import { mountTabs } from '../../components/tabs';
import { mountRelatedLinks } from '../../components/relatedLinks';
import { redirectToCable, updateUnLoggedText } from '../../util/loggedCases';
import { toggleElementVisibilityById } from '@vfde-brix/ws10/core';

export type State = ReturnType<typeof mapStateToProps>;
export type Actions = typeof mapDispatch;

function appContainer (_: State, actions: Actions) {
    injectSaga(APP_NAMESPACE, saga);
    injectReducer(APP_NAMESPACE, reducer);

    const loadedState = loadInitialAppState();

    tariffSelection();
    discountSection();
    priceSection();

    const loadingSpinner = mountLoadingSpinner(AppHtmlId.LOADING);
    const addressText = mountText(AddressHtmlId.ADDRESS_TEXT);
    const footnote = mountAccordion(AppHtmlId.DYNAMIC_FOOTNOTE, AccordionType.Footnote);

    mountOverlayComponent(actions.closeOverlay);
    mountAccordion(AppHtmlId.ACCORDION_FAQ, AccordionType.FAQ);
    mountTabs(AppHtmlId.WHICH_FIBER_TABS);
    mountRelatedLinks(AppHtmlId.RELATED_LINKS);
    mountRelatedLinks(AppHtmlId.RELATED_LINKS_OFFERS);

    actions.initStore(loadedState);

    return {
        getDerivedStateFromProps(newState: State, oldState: State) {
            const {
                activeOverlay,
                contentEnabled,
                avcResult,
                selectedTariff,
                tariffs,
                backendDown,
                gigakombi,
                ff,
                userInfo,
                isAutoOverlayOpened,
                preMarketingType,
            } = newState;
            const update = getUpdateFlags(newState, oldState);

            if (update.userInfoChanged){
                redirectToCable(userInfo);
                updateUnLoggedText(userInfo);
            }

            if (update.overlay && !userInfo?.isFixed) {
                availabilityCheck();
                leadManagementForm();
            };

            update.overlayChanged && updateOverlayContent(activeOverlay);
            update.avcOpened && trackPageView(
                selectedTariff!,
                gigakombi,
                ff,
                undefined,
                TrackingAvcStep.SHOW,
                isAutoOverlayOpened,
            );

            if (update.avcResultChanged) {
                toggleElementVisibilityById(CheckModuleHtmlId.MODULE_CONTAINER, !avcResult);
                toggleElementVisibilityById(
                    LMModuleHtmlId.MODULE_CONTAINER,
                    !contentEnabled && !preMarketingType,
                );
                toggleElementVisibilityById(LMModuleHtmlId.MODULE_SUCCESS_CONTAINER, false);
                toggleElementVisibilityById(AddressHtmlId.ADDRESS_WRAPPER, Boolean(avcResult));
                updateAddressText(addressText, avcResult?.address);
            };

            update.footnote && updateAccordionFootnote(
                footnote as Accordion,
                tariffs,
                selectedTariff,
                avcResult?.selected?.regioCharge,
                avcResult?.availability.fiber,
                ff,
            );
            update.loading && updateLoadingSpinner(loadingSpinner, newState.loading);

            toggleElementVisibilityById(AppHtmlId.APP_CONTAINER, !backendDown);
            toggleElementVisibilityById(AppHtmlId.ERROR, backendDown);

            saveStateToSession(newState.appState);
        },
    };
}

const Container = connect(mapStateToProps, mapDispatch)(appContainer);

export default Container;
